import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { apiUrl } from '../config/api';
import { hasRole } from '../utils/roles';

const API_BASE = apiUrl();
const MAX_OBJECT_OPTIONS = 40;
const MAX_EMPLOYEE_OPTIONS = 40;
const AUTO_SAVE_DELAY = 2000;
const monthNames = ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'];
const currency = (value) => {
  const num = Number(value) || 0;
  return num.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const formatDateTime = (value) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch (e) {
    return String(value);
  }
};
const now = new Date();
const registerTypeLabels = {
  standard: 'Ведомость сотрудников',
  manager: 'Ведомость менеджера',
};
const registerTypeBadges = {
  standard: { bg: '#ecfdf5', color: '#065f46' },
  manager: { bg: '#ede9fe', color: '#4c1d95' },
};
const managerSkipReasonText = {
  card_missing: 'нет актуального расчёта за период',
  manager_rows_missing: 'в карточке нет строк менеджера',
};
const statusLabels = {
  draft: 'Черновик',
  formed: 'Сформирована',
  issued: 'Выдано',
};

const statusBadges = {
  draft: { bg: '#f1f5f9', color: '#0f172a' },
  formed: { bg: '#dcfce7', color: '#15803d' },
  issued: { bg: '#dbeafe', color: '#1d4ed8' },
};

const printableStyles = `
  * { box-sizing: border-box; }
  body { font-family: 'Inter', 'Segoe UI', sans-serif; margin: 0; padding: 24px; color: #0f172a; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 18px; }
  th, td { border: 1px solid #e2e8f0; padding: 6px 8px; font-size: 13px; }
  th { background: #f8fafc; text-align: left; }
  .meta-table { margin-bottom: 24px; }
  .meta-table td { border: none; padding: 4px 6px; }
  .meta-table .title { font-size: 20px; font-weight: 700; padding: 10px 6px; }
  .meta-table .label { font-weight: 600; width: 140px; color: #475569; }
  .totals td { font-weight: 700; }
  .signature-col { width: 120px; }
  .page-break { page-break-after: always; }
`;

const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

const escapeHtml = (value = '') => String(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const normalizeSearchPart = (value) => String(value || '')
  .toLowerCase()
  .replace(/\s+/g, ' ')
  .trim();

const buildObjectSearchIndex = (obj = {}) => (
  [obj.objectName, obj.objectCodeFull || obj.objectCode, obj.address || obj.objectAddress, obj.company, obj.customer]
    .map(normalizeSearchPart)
    .filter(Boolean)
    .join(' ')
);

const buildEmployeeSearchIndex = (emp = {}) => (
  [emp.fullName, emp.position, emp.id, emp.objectLabel, emp.workObject, emp.objectCode]
    .map((value) => normalizeSearchPart(value))
    .filter(Boolean)
    .join(' ')
);

const extractObjectNameOnly = (label, fallback) => {
  const source = label || fallback;
  if (!source) return '';
  const text = String(source);
  const parts = text.split('·');
  if (parts.length > 1) return parts[0].trim();
  return text.trim();
};

const ensureObjectSearchMeta = (obj) => {
  if (!obj) return null;
  if (obj.searchIndex) return obj;
  return { ...obj, searchIndex: buildObjectSearchIndex(obj) };
};

const blankRow = () => ({
  id: null,
  localId: `row-${Math.random().toString(36).slice(2)}`,
  employeeId: null,
  fullName: '',
  position: '',
  paidToCard: 0,
  advanceAmount: 0,
  incomeAmount: 0,
  toPayAmount: 0,
  notes: '',
});

// По умолчанию берём предыдущий месяц относительно текущей даты
const defaultSheet = (month = ((now.getMonth() + 12) % 12) || 12, year = (now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear()), registerType = 'standard') => ({
  id: null,
  title: 'Ведомость выплат',
  serviceName: '',
  unitName: 'Клининг',
  objectName: '',
  objectCode: '',
  periodMonth: month,
  periodYear: year,
  notes: '',
  registerType,
  cardId: null,
  managerUserId: null,
  fotLimit: 0,
  status: 'draft',
  signedBy: null,
  signedAt: null,
  issuedBy: null,
  issuedAt: null,
  rows: [blankRow()],
});

const normalizeLimitUsage = (raw = {}) => ({
  issued: round2(Number(raw.issued) || 0),
  locked: round2(Number(raw.locked) || 0),
});

const deriveLimitInfo = ({ fotLimit = 0, usage = normalizeLimitUsage(), currentIncome = 0, registerType = 'standard' }) => {
  if (registerType === 'manager') {
    return {
      fotLimit: 0,
      usedIssued: 0,
      usedLocked: 0,
      available: 0,
      registerIncome: round2(currentIncome),
      forecastAfterSign: 0,
      exceeds: false,
      warnLocked: false,
    };
  }

  const limit = round2(fotLimit);
  const usedIssued = round2(Number(usage.issued) || 0);
  const usedLocked = round2(Number(usage.locked) || 0);
  const registerIncome = round2(currentIncome);
  const available = round2(limit - usedIssued - usedLocked);
  const forecastAfterSign = round2(available - registerIncome);

  return {
    fotLimit: limit,
    usedIssued,
    usedLocked,
    available,
    registerIncome,
    forecastAfterSign,
    exceeds: forecastAfterSign < 0,
    warnLocked: usedLocked > 0 && forecastAfterSign >= 0,
  };
};

const pageWrapper = {
  width: '100%',
  maxWidth: '100%',
  margin: '0 auto',
  padding: '24px clamp(12px, 4vw, 36px)',
};

const panel = {
  background: '#fff',
  borderRadius: 24,
  boxShadow: '0 25px 60px rgba(15,23,42,0.08)',
  marginBottom: 28,
};

const labelStyle = {
  fontSize: 12,
  fontWeight: 600,
  color: '#475569',
  marginBottom: 6,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 12,
  border: '1px solid #cbd5f5',
  background: '#fff',
  fontSize: 14,
  transition: 'border-color 0.15s ease',
};

const selectStyle = {
  ...inputStyle,
  appearance: 'none',
};

const tableWrapper = {
  overflowX: 'visible',
  marginTop: 20,
  borderRadius: 18,
  border: '1px solid #e2e8f0',
  width: '100%',
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'separate',
  borderSpacing: 0,
  minWidth: 'auto',
};

const thStyle = {
  textAlign: 'left',
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  padding: '8px 10px',
  background: '#f8fafc',
  borderBottom: '1px solid #e2e8f0',
  color: '#475569',
};

const tdStyle = {
  padding: '8px 10px',
  borderBottom: '1px solid #e2e8f0',
  background: '#fff',
  verticalAlign: 'top',
};

const parseNumber = (value) => {
  if (value === '' || value === null || value === undefined) return 0;
  const normalized = String(value).replace(/\s/g, '').replace(',', '.');
  const num = Number(normalized);
  return Number.isFinite(num) ? round2(num) : 0;
};

const calcToPayAmount = (row = {}) => {
  const income = parseNumber(row.incomeAmount);
  const advance = parseNumber(row.advanceAmount);
  const paidCard = parseNumber(row.paidToCard);
  return round2(income - advance - paidCard);
};

const parseDateSafe = (value) => {
  if (!value) return null;
  try {
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return null;
    return dt;
  } catch {
    return null;
  }
};

const isDateInPast = (value) => {
  const dt = parseDateSafe(value);
  if (!dt) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  dt.setHours(0, 0, 0, 0);
  return dt <= today;
};

const formatEmployeeDate = (value) => {
  const dt = parseDateSafe(value);
  if (!dt) return '—';
  return dt.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const firedAccentColor = '#b91c1c';
const employeeSuggestionListStyle = {
  position: 'absolute',
  top: 'calc(100% + 8px)',
  left: 0,
  zIndex: 8,
  minWidth: 420,
  width: 'min(600px, calc(100vw - 48px))',
  maxWidth: 'min(640px, calc(100vw - 32px))',
  maxHeight: 'min(380px, calc(100vh - 220px))',
  overflowY: 'auto',
  border: '1px solid #e2e8f0',
  borderRadius: 16,
  background: '#fff',
  boxShadow: '0 22px 48px rgba(15,23,42,0.22)',
};

const employeeSuggestionItemStyle = {
  width: '100%',
  textAlign: 'left',
  border: 'none',
  background: '#fff',
  padding: '10px 12px',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  fontSize: 13,
  color: '#0f172a',
};

const employeeSuggestionMetaStyle = {
  fontSize: 11,
  color: '#475569',
};

const employeeMetaValueStyle = {
  fontSize: 11,
  color: '#475569',
  lineHeight: 1.3,
};

const extractObjectCode = (value) => {
  const text = String(value || '').trim();
  if (!text) return '';
  const match = text.match(/([0-9]{2,}[0-9-]*)$/);
  if (match) return match[1];
  return text;
};

const formatObjectCodeCell = (value) => {
  const code = extractObjectCode(value);
  return code || '—';
};

const mapRowFromApi = (row = {}) => {
  const mapped = {
    id: row.id || null,
    localId: `row-${row.id || Math.random().toString(36).slice(2)}`,
    employeeId: row.employeeId || null,
    fullName: row.fullName || '',
    position: row.position || '',
    paidToCard: row.paidToCard ?? row.paid_to_card ?? 0,
    advanceAmount: row.advanceAmount ?? row.advance_amount ?? 0,
    incomeAmount: row.incomeAmount ?? row.income_amount ?? 0,
    toPayAmount: row.toPayAmount ?? row.to_pay_amount ?? 0,
    notes: row.notes || '',
  };
  return { ...mapped, toPayAmount: calcToPayAmount(mapped) };
};

const mapEmployeeFromApi = (row = {}) => {
  const fullName = row.full_name || row.fullName || '';
  const contractFrom = row.contract_from || row.contractFrom || row.contract_date || row.contractDate || null;
  const contractTo = row.contract_to || row.contractTo || null;
  const workObject = row.work_object || row.workObject || '';
  const workAddress = row.work_address || row.workAddress || '';
  const explicitLabel = row.object_label || row.objectLabel || '';
  const combinedLabel = explicitLabel || (workObject && workAddress
    ? `${workObject} · ${workAddress}`
    : workObject || workAddress || '');
  const objectDisplay = extractObjectNameOnly(explicitLabel, workObject || workAddress || combinedLabel);
  const normalized = {
    id: row.id ?? row.employee_id ?? null,
    fullName,
    position: row.position || '',
    contractFrom,
    contractTo,
    workObject,
    workAddress,
    objectLabel: combinedLabel || null,
    objectDisplay: objectDisplay || null,
    objectCode: row.object_code || row.objectCode || null,
    hireDateLabel: formatEmployeeDate(contractFrom),
    fireDateLabel: formatEmployeeDate(contractTo),
    isDismissed: isDateInPast(contractTo),
  };
  if (!normalized.id) return null;
  return { ...normalized, searchIndex: buildEmployeeSearchIndex(normalized) };
};

function buildPrintableRows(rows = []) {
  return rows
    .filter((row) => String(row.fullName || '').trim())
    .map((row, index) => `
      <tr>
        <td>${index + 1}</td>
        <td>${escapeHtml(row.fullName)}</td>
        <td>${escapeHtml(row.position || '')}</td>
        <td style="text-align:right">${currency(row.paidToCard)}</td>
        <td style="text-align:right">${currency(row.advanceAmount)}</td>
        <td style="text-align:right">${currency(row.incomeAmount)}</td>
        <td style="text-align:right">${currency(calcToPayAmount(row))}</td>
        <td class="signature-col"></td>
      </tr>
    `).join('');
}

function buildPrintDocument(sheet, rows, mode = 'full') {
  const monthTitle = monthNames[(Number(sheet.periodMonth) || 1) - 1] || sheet.periodMonth;
  const typeTitle = sheet.registerType === 'manager' ? 'Ведомость менеджера' : 'Ведомость сотрудников';
  const headerHtml = `
    <table class="meta-table">
      <tr><td class="title" colspan="8">${escapeHtml(sheet.title || 'Ведомость выплат')}</td></tr>
      <tr>
        <td class="label">Подразделение</td>
        <td colspan="2">${escapeHtml(sheet.unitName || '—')}</td>
        <td class="label">Объект</td>
        <td colspan="4">${escapeHtml(sheet.objectName || '')}</td>
      </tr>
      <tr>
        <td class="label">Код/№</td>
        <td colspan="2">${escapeHtml(sheet.objectCode || '')}</td>
        <td class="label">Месяц, год</td>
        <td colspan="4">${escapeHtml(`${monthTitle}, ${sheet.periodYear}`)}</td>
      </tr>
      <tr>
        <td class="label">Тип ведомости</td>
        <td colspan="2">${escapeHtml(typeTitle)}</td>
        <td class="label">Примечание</td>
        <td colspan="4">${escapeHtml(sheet.notes || '—')}</td>
      </tr>
    </table>
  `;

  if (mode === 'per-employee') {
    return rows.map((row, idx) => `
      ${headerHtml}
      <table>
        <thead>
          <tr>
            <th>№</th>
            <th>Фамилия, инициалы</th>
            <th>Должность</th>
            <th>Выплачено на карту, руб.</th>
            <th>Аванс, руб.</th>
            <th>Итого доход за месяц, руб.</th>
            <th>К выплате, руб.</th>
            <th>Подпись сотрудника</th>
          </tr>
        </thead>
        <tbody>
          ${buildPrintableRows([row])}
        </tbody>
      </table>
      ${idx < rows.length - 1 ? '<div class="page-break"></div>' : ''}
    `).join('');
  }

  const totals = rows.reduce((acc, row) => ({
    paidToCard: acc.paidToCard + (Number(row.paidToCard) || 0),
    advanceAmount: acc.advanceAmount + (Number(row.advanceAmount) || 0),
    incomeAmount: acc.incomeAmount + (Number(row.incomeAmount) || 0),
    toPayAmount: acc.toPayAmount + calcToPayAmount(row),
  }), { paidToCard: 0, advanceAmount: 0, incomeAmount: 0, toPayAmount: 0 });

  return `
    ${headerHtml}
    <table>
      <thead>
        <tr>
          <th>№</th>
          <th>Фамилия, инициалы</th>
          <th>Должность</th>
          <th>Выплачено на карту, руб.</th>
          <th>Аванс, руб.</th>
          <th>Итого доход за месяц, руб.</th>
          <th>К выплате, руб.</th>
          <th>Подпись сотрудника</th>
        </tr>
      </thead>
      <tbody>
        ${buildPrintableRows(rows)}
        <tr class="totals">
          <td colspan="3">ИТОГО:</td>
          <td style="text-align:right">${currency(totals.paidToCard)}</td>
          <td style="text-align:right">${currency(totals.advanceAmount)}</td>
          <td style="text-align:right">${currency(totals.incomeAmount)}</td>
          <td style="text-align:right">${currency(totals.toPayAmount)}</td>
          <td></td>
        </tr>
      </tbody>
    </table>
  `;
}

export function buildManagerWithObjectsDocument(sheet, rows, previewData, objectRegisters = []) {
  const monthTitle = monthNames[(Number(sheet.periodMonth) || 1) - 1] || sheet.periodMonth;
  const sharedColgroup = `
    <col style="width:6%" />
    <col style="width:28%" />
    <col style="width:15%" />
    <col style="width:13%" />
    <col style="width:13%" />
    <col style="width:12%" />
    <col style="width:13%" />
  `;
  const headerHtml = `
    <table class="meta-table">
      <tr><td class="title" colspan="8">${escapeHtml(sheet.title || 'Ведомость менеджера')}</td></tr>
      <tr>
        <td class="label">Менеджер</td>
        <td colspan="2">${escapeHtml(sheet.objectName || sheet.notes || '—')}</td>
        <td class="label">ID менеджера</td>
        <td colspan="4">${escapeHtml(sheet.managerUserId || sheet.objectCode || '')}</td>
      </tr>
      <tr>
        <td class="label">Месяц, год</td>
        <td colspan="2">${escapeHtml(`${monthTitle}, ${sheet.periodYear}`)}</td>
        <td class="label">Период</td>
        <td colspan="4">${escapeHtml(previewData?.period ? `${previewData.period.start} — ${previewData.period.end}` : `${monthTitle}`)}</td>
      </tr>
    </table>
  `;

  const managerRows = rows.filter((row) => String(row.fullName || '').trim());
  const managerTotals = managerRows.reduce((acc, row) => ({
    paidToCard: round2(acc.paidToCard + (Number(row.paidToCard) || 0)),
    advanceAmount: round2(acc.advanceAmount + (Number(row.advanceAmount) || 0)),
    incomeAmount: round2(acc.incomeAmount + (Number(row.incomeAmount) || 0)),
    toPayAmount: round2(acc.toPayAmount + calcToPayAmount(row)),
  }), { paidToCard: 0, advanceAmount: 0, incomeAmount: 0, toPayAmount: 0 });

  const objectTotals = objectRegisters.reduce((acc, reg) => ({
    paidToCard: round2(acc.paidToCard + (Number(reg.totalPaidCard) || 0)),
    advanceAmount: round2(acc.advanceAmount + (Number(reg.totalAdvance) || 0)),
    incomeAmount: round2(acc.incomeAmount + (Number(reg.totalIncome) || 0)),
    toPayAmount: round2(acc.toPayAmount + (Number(reg.totalToPay) || 0)),
  }), { paidToCard: 0, advanceAmount: 0, incomeAmount: 0, toPayAmount: 0 });

  const overallTotals = {
    paidToCard: round2(objectTotals.paidToCard + managerTotals.paidToCard),
    advanceAmount: round2(objectTotals.advanceAmount + managerTotals.advanceAmount),
    incomeAmount: round2(objectTotals.incomeAmount + managerTotals.incomeAmount),
    toPayAmount: round2(objectTotals.toPayAmount + managerTotals.toPayAmount),
  };

  const objectRowsHtml = objectRegisters.map((reg, idx) => `
      <tr>
        <td>${idx + 1}</td>
        <td>${escapeHtml(reg.objectName || 'Объект')}</td>
        <td>${escapeHtml(formatObjectCodeCell(reg.objectCode))}</td>
        <td style="text-align:right">${currency(reg.totalPaidCard)}</td>
        <td style="text-align:right">${currency(reg.totalAdvance)}</td>
        <td style="text-align:right">${currency(reg.totalIncome)}</td>
        <td style="text-align:right">${currency(reg.totalToPay)}</td>
      </tr>
    `).join('');

  const managerRowsHtml = managerRows
    .map((row, index) => `
      <tr>
        <td>${index + 1}</td>
        <td>${escapeHtml(row.fullName || '')}</td>
        <td>${escapeHtml(formatObjectCodeCell(row.position))}</td>
        <td style="text-align:right">${currency(row.paidToCard)}</td>
        <td style="text-align:right">${currency(row.advanceAmount)}</td>
        <td style="text-align:right">${currency(row.incomeAmount)}</td>
        <td style="text-align:right">${currency(calcToPayAmount(row))}</td>
      </tr>
    `)
    .join('');

  return `
    ${headerHtml}
    <h2 style="margin:16px 0 6px">Объекты менеджера</h2>
    <table>
      <colgroup>${sharedColgroup}</colgroup>
      <thead>
        <tr>
          <th>№</th>
          <th>Объект / описание</th>
          <th>Код объекта</th>
          <th>Выплачено на карту</th>
          <th>Аванс</th>
          <th>Итого доход</th>
          <th>К выплате</th>
        </tr>
      </thead>
      <tbody>
        ${objectRowsHtml || '<tr><td colspan="7">Нет подписанных ведомостей по объектам</td></tr>'}
        <tr class="totals">
          <td colspan="3">ИТОГО по объектам:</td>
          <td style="text-align:right">${currency(objectTotals.paidToCard)}</td>
          <td style="text-align:right">${currency(objectTotals.advanceAmount)}</td>
          <td style="text-align:right">${currency(objectTotals.incomeAmount)}</td>
          <td style="text-align:right">${currency(objectTotals.toPayAmount)}</td>
        </tr>
      </tbody>
    </table>
    <h2 style="margin:18px 0 6px">Менеджер</h2>
    <table>
      <colgroup>${sharedColgroup}</colgroup>
      <thead>
        <tr>
          <th>№</th>
          <th>Объект / описание</th>
          <th>Код объекта</th>
          <th>Выплачено на карту</th>
          <th>Аванс</th>
          <th>Итого доход</th>
          <th>К выплате</th>
        </tr>
      </thead>
      <tbody>
        ${managerRowsHtml || '<tr><td colspan="7">Нет строк по менеджеру</td></tr>'}
        <tr class="totals">
          <td colspan="3">ИТОГО менеджер:</td>
          <td style="text-align:right">${currency(managerTotals.paidToCard)}</td>
          <td style="text-align:right">${currency(managerTotals.advanceAmount)}</td>
          <td style="text-align:right">${currency(managerTotals.incomeAmount)}</td>
          <td style="text-align:right">${currency(managerTotals.toPayAmount)}</td>
        </tr>
      </tbody>
    </table>
    <table>
      <colgroup>${sharedColgroup}</colgroup>
      <tbody>
        <tr class="totals">
          <td colspan="3" style="font-weight:700">ВСЕГО (объекты + менеджер):</td>
          <td style="text-align:right">${currency(overallTotals.paidToCard)}</td>
          <td style="text-align:right">${currency(overallTotals.advanceAmount)}</td>
          <td style="text-align:right">${currency(overallTotals.incomeAmount)}</td>
          <td style="text-align:right">${currency(overallTotals.toPayAmount)}</td>
        </tr>
      </tbody>
    </table>
    <div style="margin-top:24px; font-size:13px;">
      Подпись менеджера: ________________________________
    </div>
  `;
}

export function openPrintWindow(html) {
  const win = window.open('', '_blank', 'width=1000,height=800');
  if (!win) {
    alert('Браузер заблокировал всплывающее окно. Разрешите открытие окна печати.');
    return;
  }
  win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Ведомость выплат</title><style>${printableStyles}</style></head><body>${html}</body></html>`);
  win.document.close();
  win.focus();
  setTimeout(() => {
    try { win.print(); } catch {}
  }, 300);
}

export default function PayrollRegisterCard() {
  const { id: routeId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const requestedType = useMemo(() => (searchParams.get('type') === 'manager' ? 'manager' : 'standard'), [searchParams]);

  const token = useMemo(() => (typeof window !== 'undefined' ? localStorage.getItem('authToken') : ''), []);
  const authHeaders = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  const [detailLoading, setDetailLoading] = useState(false);
  const [form, setForm] = useState(() => defaultSheet(undefined, undefined, requestedType));
  const [saving, setSaving] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [positions, setPositions] = useState([]);
  const [objects, setObjects] = useState([]);
  const [objectsLoading, setObjectsLoading] = useState(false);
  const [objectError, setObjectError] = useState('');
  const [cardInfo, setCardInfo] = useState(null);
  const [managerInfo, setManagerInfo] = useState(null);
  const [managers, setManagers] = useState([]);
  const [managersLoading, setManagersLoading] = useState(false);
  const [managerError, setManagerError] = useState('');
  const [managerPreview, setManagerPreview] = useState(null);
  const [managerPreviewLoading, setManagerPreviewLoading] = useState(false);
  const [managerPreviewError, setManagerPreviewError] = useState('');
  const [limitUsage, setLimitUsage] = useState(() => normalizeLimitUsage());
  const [statusMessage, setStatusMessage] = useState('');
  const [autoSaveState, setAutoSaveState] = useState('idle');
  const [autoSaveError, setAutoSaveError] = useState('');
  const [dirtyKey, setDirtyKey] = useState(0);
  const autoSaveTimerRef = useRef(null);
  const lastSavedKeyRef = useRef(0);
  const saveRef = useRef(null);
  const managerAutoKeyRef = useRef('');
  const employeeDropdownTimerRef = useRef(null);
  const pendingRowDirtyRef = useRef(new Set());
  const activeRowRef = useRef(null);
  const [signing, setSigning] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [copyingPrevious, setCopyingPrevious] = useState(false);
  const [employeeSearchMap, setEmployeeSearchMap] = useState({});
  const [objectSearchTerm, setObjectSearchTerm] = useState('');
  const [activeEmployeeDropdown, setActiveEmployeeDropdown] = useState(null);

  useEffect(() => {
    let ignore = false;
    const loadEmployees = async () => {
      try {
        const resp = await fetch(`${API_BASE}/api/employees`, { headers: { ...authHeaders } });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data?.error || 'employees_load_failed');
        if (ignore) return;
        const mapped = (Array.isArray(data) ? data : [])
          .map(mapEmployeeFromApi)
          .filter(Boolean)
          .sort((a, b) => (a.fullName || '').localeCompare(b.fullName || '', 'ru', { sensitivity: 'base' }));
        setEmployees(mapped);
      } catch (e) {
        if (ignore) return;
        console.error('employees load error', e);
        setEmployees([]);
      }
    };
    loadEmployees();
    return () => { ignore = true; };
  }, [authHeaders]);

  useEffect(() => {
    let ignore = false;
    const loadPositions = async () => {
      try {
        const resp = await fetch(`${API_BASE}/api/positions`, { headers: { ...authHeaders } });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data?.error || 'positions_load_failed');
        if (ignore) return;
        const mapped = (Array.isArray(data) ? data : [])
          .map((item) => ({ id: item.id, name: item.name || '' }))
          .filter((item) => item.id && item.name)
          .sort((a, b) => a.name.localeCompare(b.name, 'ru', { sensitivity: 'base' }));
        setPositions(mapped);
      } catch (e) {
        if (ignore) return;
        console.error('positions load error', e);
        setPositions([]);
      }
    };
    loadPositions();
    return () => { ignore = true; };
  }, [authHeaders]);

  useEffect(() => {
    if (form.registerType !== 'manager') {
      setManagerError('');
      setManagersLoading(false);
      return;
    }
    let ignore = false;
    setManagersLoading(true);
    setManagerError('');
    const loadManagers = async () => {
      try {
        const resp = await fetch(`${API_BASE}/api/users`, { headers: { ...authHeaders } });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data?.error || 'managers_load_failed');
        if (ignore) return;
        const items = (Array.isArray(data) ? data : [])
          .filter((user) => hasRole(user, 'manager'))
          .map((user) => ({ id: String(user.id || '').trim(), name: user.name || `ID ${user.id}` }))
          .filter((item) => item.id)
          .sort((a, b) => a.name.localeCompare(b.name, 'ru', { sensitivity: 'base' }));
        setManagers(items);
        setManagerError('');
      } catch (e) {
        if (ignore) return;
        console.error('managers load error', e);
        setManagers([]);
        setManagerError('Не удалось загрузить список менеджеров');
      } finally {
        if (!ignore) setManagersLoading(false);
      }
    };
    loadManagers();
    return () => { ignore = true; };
  }, [form.registerType, authHeaders]);

  const periodMonth = form.periodMonth;
  const periodYear = form.periodYear;
  const markDirty = useCallback(() => {
    if (form.status !== 'draft') return;
    setDirtyKey((key) => key + 1);
  }, [form.status]);
  const hasFilledRows = useMemo(() => (form.rows || []).some((row) => String(row.fullName || '').trim()), [form.rows]);
  const totals = useMemo(() => {
    const sum = (key) => (form.rows || []).reduce((acc, row) => acc + (Number(row[key]) || 0), 0);
    return {
      paidToCard: sum('paidToCard'),
      advanceAmount: sum('advanceAmount'),
      incomeAmount: sum('incomeAmount'),
      toPayAmount: round2((form.rows || []).reduce((acc, row) => acc + calcToPayAmount(row), 0)),
    };
  }, [form.rows]);
  const currentFotLimit = useMemo(() => {
    if (form.registerType === 'manager') return 0;
    if (cardInfo?.fotWithoutManager) return cardInfo.fotWithoutManager;
    return Number(form.fotLimit) || 0;
  }, [cardInfo, form.registerType, form.fotLimit]);
  const managerFot = useMemo(() => cardInfo?.managerFot || 0, [cardInfo]);
  const selectedManager = useMemo(() => {
    if (form.registerType !== 'manager') return null;
    const byId = managers.find((m) => String(m.id) === String(form.managerUserId));
    return byId || managerInfo || managerPreview?.manager || null;
  }, [form.registerType, form.managerUserId, managers, managerInfo, managerPreview]);
  const managerPreviewTotals = managerPreview?.totals || { income: 0, cash: 0, card: 0, count: 0 };
  const managerPreviewFresh = useMemo(() => {
    if (!managerPreview || form.registerType !== 'manager') return null;
    const previewManagerId = String(managerPreview.manager?.id || managerPreview.managerUserId || '');
    const currentManagerId = String(form.managerUserId || '');
    const sameManager = previewManagerId && previewManagerId === currentManagerId;
    const samePeriod = Number(managerPreview.month) === Number(form.periodMonth) && Number(managerPreview.year) === Number(form.periodYear);
    return sameManager && samePeriod ? managerPreview : null;
  }, [managerPreview, form.registerType, form.managerUserId, form.periodMonth, form.periodYear]);

  const currentStatus = String(form.status || 'draft').toLowerCase();
  const currentStatusLabel = statusLabels[currentStatus] || statusLabels.draft;
  const statusBadgeStyle = statusBadges[currentStatus] || statusBadges.draft;
  const isLocked = currentStatus !== 'draft';
  const hasLinkedEntity = form.registerType === 'manager'
    ? Boolean(form.managerUserId)
    : Boolean(form.cardId);
  const canAutoSave = !isLocked && hasLinkedEntity;
  const canSign = Boolean(form.id) && currentStatus === 'draft';
  const canIssue = Boolean(form.id) && currentStatus === 'formed';
  const canUnlock = Boolean(form.id) && currentStatus !== 'draft';

  const limitInfo = useMemo(() => deriveLimitInfo({
    fotLimit: currentFotLimit,
    usage: limitUsage,
    currentIncome: totals.incomeAmount,
    registerType: form.registerType,
  }), [currentFotLimit, limitUsage, totals.incomeAmount, form.registerType]);

  const limitBlockedByFot = form.registerType !== 'manager' && limitInfo.exceeds;
  const limitWarnByLocked = form.registerType !== 'manager' && !limitInfo.exceeds && limitInfo.warnLocked;
  const limitExceeded = limitInfo.exceeds;

  const autoSaveInfo = useMemo(() => {
    switch (autoSaveState) {
      case 'saving':
        return { text: 'сохраняем изменения…', color: '#0284c7' };
      case 'saved':
        return { text: 'изменения сохранены', color: '#16a34a' };
      case 'error':
        return { text: autoSaveError || 'ошибка автосохранения', color: '#b91c1c' };
      case 'blocked':
        return { text: autoSaveError || 'автосохранение недоступно', color: '#b45309' };
      case 'pending':
        return { text: 'требуется сохранить', color: '#c2410c' };
      default:
        return { text: 'ожидание изменений', color: '#475569' };
    }
  }, [autoSaveState, autoSaveError]);
  const loadObjects = useCallback(async () => {
    if (!periodMonth || !periodYear) return;
    setObjectsLoading(true);
    setObjectError('');
    try {
      const params = new URLSearchParams({ month: String(periodMonth), year: String(periodYear) });
      const resp = await fetch(`${API_BASE}/api/payrolls/meta/objects?${params.toString()}`, { headers: { ...authHeaders } });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.error || 'objects_load_failed');
      const rows = Array.isArray(data.items) ? data.items : [];
      setObjects(rows.map((item) => ensureObjectSearchMeta(item)));
    } catch (e) {
      console.error('objects load error', e);
      setObjects([]);
      setObjectError('Не удалось загрузить список объектов');
    } finally {
      setObjectsLoading(false);
    }
  }, [authHeaders, periodMonth, periodYear]);

  useEffect(() => {
    if (form.registerType === 'manager') {
      setObjects([]);
      setObjectsLoading(false);
      setObjectError('');
      return;
    }
    loadObjects();
  }, [loadObjects, form.registerType]);

  const objectOptions = useMemo(() => {
    if (form.registerType === 'manager') return [];
    if (!form.cardId) return objects;
    const exists = objects.some((obj) => Number(obj.cardId) === Number(form.cardId));
    if (exists) return objects;
    if (cardInfo) return [ensureObjectSearchMeta(cardInfo), ...objects];
    const fallback = ensureObjectSearchMeta({
      cardId: form.cardId,
      objectName: form.objectName,
      objectCode: form.objectCode,
      objectCodeFull: form.objectCode,
      fotWithoutManager: form.fotLimit,
    });
    return fallback ? [fallback, ...objects] : objects;
  }, [objects, form.registerType, form.cardId, cardInfo, form.objectName, form.objectCode, form.fotLimit]);
  const normalizedObjectQuery = normalizeSearchPart(objectSearchTerm);
  const objectSelectData = useMemo(() => {
    if (form.registerType === 'manager') return { options: [], hasMatches: true };
    if (!normalizedObjectQuery) return { options: objectOptions, hasMatches: true };
    const filtered = objectOptions.filter((obj) => (obj.searchIndex || buildObjectSearchIndex(obj)).includes(normalizedObjectQuery));
    let limited = filtered.slice(0, MAX_OBJECT_OPTIONS);
    const selectedId = Number(form.cardId) || null;
    if (selectedId) {
      const selected = objectOptions.find((obj) => Number(obj.cardId) === selectedId);
      if (selected && !limited.some((obj) => Number(obj.cardId) === selectedId)) {
        limited = [selected, ...limited];
      }
    }
    return { options: limited, hasMatches: filtered.length > 0 };
  }, [objectOptions, normalizedObjectQuery, form.cardId, form.registerType]);

  const managerOptions = useMemo(() => {
    if (form.registerType !== 'manager') return [];
    const hasCurrent = managers.some((m) => String(m.id) === String(form.managerUserId));
    const fallbackName = selectedManager?.name || managerInfo?.name || '';
    if (hasCurrent || !form.managerUserId || !fallbackName) return managers;
    return [{ id: form.managerUserId, name: fallbackName }, ...managers];
  }, [form.registerType, managers, form.managerUserId, selectedManager, managerInfo]);

  useEffect(() => {
    if (!routeId) {
      setDetailLoading(false);
      setCardInfo(null);
      setManagerInfo(null);
      setManagerPreview(null);
      setLimitUsage(normalizeLimitUsage());
      setForm((prev) => {
        if (!prev.id && prev.registerType === requestedType) return prev;
        return defaultSheet(undefined, undefined, requestedType);
      });
      return;
    }
    let ignore = false;
    const loadRegister = async () => {
      setDetailLoading(true);
      try {
        const resp = await fetch(`${API_BASE}/api/payrolls/${routeId}`, { headers: { ...authHeaders } });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data?.error || 'load_failed');
        if (ignore) return;
        setForm({
          id: data.id,
          title: data.title,
          serviceName: data.serviceName,
          unitName: data.unitName,
          objectName: data.objectName,
          objectCode: data.objectCode,
          periodMonth: data.periodMonth,
          periodYear: data.periodYear,
          notes: data.notes || '',
          registerType: data.registerType || 'standard',
          cardId: data.cardId || null,
          managerUserId: data.managerUserId || null,
          fotLimit: data.fotLimit || 0,
          status: data.status || 'draft',
          signedBy: data.signedBy || null,
          signedAt: data.signedAt || null,
          issuedBy: data.issuedBy || null,
          issuedAt: data.issuedAt || null,
          rows: (data.rows || []).map(mapRowFromApi).concat([blankRow()]),
        });
        setCardInfo(ensureObjectSearchMeta(data.cardInfo));
        setManagerInfo(data.managerInfo || null);
        setManagerPreview(null);
        setLimitUsage(normalizeLimitUsage(data.limit));
      } catch (e) {
        if (!ignore) console.error('load register error', e);
      } finally {
        if (!ignore) setDetailLoading(false);
      }
    };
    loadRegister();
    return () => { ignore = true; };
  }, [routeId, authHeaders, requestedType]);

  useEffect(() => {
    if (form.registerType !== 'manager') {
      setManagerPreview(null);
      setManagerPreviewError('');
      return;
    }
    setManagerPreviewError('');
    setManagerPreview((prev) => {
      if (!prev) return prev;
      const sameManager = String(prev.manager?.id || '') === String(form.managerUserId || '');
      const samePeriod = Number(prev.month) === Number(form.periodMonth) && Number(prev.year) === Number(form.periodYear);
      return sameManager && samePeriod ? prev : null;
    });
  }, [form.registerType, form.managerUserId, form.periodMonth, form.periodYear]);

  useEffect(() => {
    if (form.registerType !== 'manager') {
      managerAutoKeyRef.current = '';
    }
  }, [form.registerType]);

  useEffect(() => {
    if (form.registerType === 'manager' && objectSearchTerm) {
      setObjectSearchTerm('');
    }
  }, [form.registerType, objectSearchTerm]);

  useEffect(() => {
    const validIds = new Set((form.rows || []).map((row) => String(row.localId)));
    setEmployeeSearchMap((prev) => {
      let mutated = false;
      const next = {};
      for (const [key, value] of Object.entries(prev)) {
        if (validIds.has(String(key))) {
          next[key] = value;
        } else {
          mutated = true;
        }
      }
      return mutated ? next : prev;
    });
    pendingRowDirtyRef.current.forEach((rowId) => {
      if (!validIds.has(String(rowId))) {
        pendingRowDirtyRef.current.delete(rowId);
        if (activeRowRef.current === rowId) {
          activeRowRef.current = null;
        }
      }
    });
  }, [form.rows]);

  useEffect(() => {
    if (!activeEmployeeDropdown) return;
    const exists = (form.rows || []).some((row) => String(row.localId) === String(activeEmployeeDropdown));
    if (!exists) setActiveEmployeeDropdown(null);
  }, [form.rows, activeEmployeeDropdown]);

  const updateField = (field, value) => {
    if (isLocked) return;
    setForm((prev) => ({ ...prev, [field]: value }));
    markDirty();
  };

  const applyCardSelection = useCallback((info) => {
    if (!info) {
      setCardInfo(null);
      setLimitUsage(normalizeLimitUsage());
      setForm((prev) => ({
        ...prev,
        cardId: null,
        objectName: '',
        objectCode: '',
        fotLimit: 0,
      }));
      return;
    }
    setCardInfo(ensureObjectSearchMeta(info));
    setLimitUsage(normalizeLimitUsage(info.limitUsage));
    setForm((prev) => ({
      ...prev,
      cardId: info.cardId,
      objectName: info.objectName || '',
      objectCode: info.objectCodeFull || info.objectCode || '',
      fotLimit: info.fotWithoutManager || 0,
    }));
  }, []);

  const ensureExtraBlankRow = useCallback((rows) => {
    const trimmed = rows.filter((row, idx) => {
      if (idx === rows.length - 1) return true;
      const hasContent = String(row.fullName || '').trim() || String(row.position || '').trim() || Number(row.paidToCard) || Number(row.advanceAmount) || Number(row.incomeAmount) || Number(row.toPayAmount);
      return hasContent || idx === 0;
    });
    const last = trimmed[trimmed.length - 1];
    if (!last || String(last.fullName || '').trim()) {
      trimmed.push(blankRow());
    }
    return trimmed;
  }, []);

  const resetPendingRows = useCallback(() => {
    pendingRowDirtyRef.current.clear();
    activeRowRef.current = null;
  }, []);

  const commitRowDirty = useCallback((rowId) => {
    if (!rowId) return;
    const key = String(rowId);
    if (!pendingRowDirtyRef.current.has(key)) return;
    pendingRowDirtyRef.current.delete(key);
    markDirty();
  }, [markDirty]);

  const handleRowFocusChange = useCallback((rowId) => {
    const key = String(rowId);
    if (activeRowRef.current && activeRowRef.current !== key) {
      commitRowDirty(activeRowRef.current);
    }
    activeRowRef.current = key;
  }, [commitRowDirty]);

  const handleRowBlurChange = useCallback((rowId, event) => {
    const key = String(rowId);
    const nextTarget = event?.relatedTarget;
    if (nextTarget && event.currentTarget?.contains(nextTarget)) return;
    commitRowDirty(key);
    if (activeRowRef.current === key) {
      activeRowRef.current = null;
    }
  }, [commitRowDirty]);

  const updateRow = useCallback((rowId, patch) => {
    if (isLocked) return;
    const affectsToPay = ['paidToCard', 'advanceAmount', 'incomeAmount'].some((key) => key in patch);
    setForm((prev) => {
      const rows = prev.rows.map((row) => {
        if (row.localId !== rowId) return row;
        const nextRow = { ...row, ...patch };
        if (affectsToPay) {
          nextRow.toPayAmount = calcToPayAmount(nextRow);
        }
        return nextRow;
      });
      const cleaned = ensureExtraBlankRow(rows);
      return { ...prev, rows: cleaned };
    });
    pendingRowDirtyRef.current.add(String(rowId));
  }, [ensureExtraBlankRow, isLocked]);

  const handleObjectSelect = (value) => {
    if (isLocked) return;
    const nextId = Number(value) || null;
    if (!nextId) {
      applyCardSelection(null);
      markDirty();
      return;
    }
    const info = objectOptions.find((obj) => Number(obj.cardId) === nextId);
    applyCardSelection(info || null);
    markDirty();
  };

  const getEmployeeOptions = useCallback((row) => {
    const hasCustomQuery = Object.prototype.hasOwnProperty.call(employeeSearchMap, row.localId);
    const rawQuery = hasCustomQuery ? employeeSearchMap[row.localId] : row.fullName;
    const term = normalizeSearchPart(rawQuery);
    const source = employees;
    const selectedId = row.employeeId ? String(row.employeeId) : '';
    const selected = selectedId ? source.find((emp) => String(emp.id) === selectedId) : null;
    if (!term) {
      let base = source.length > MAX_EMPLOYEE_OPTIONS ? source.slice(0, MAX_EMPLOYEE_OPTIONS) : source;
      if (selected && !base.some((emp) => String(emp.id) === selectedId)) {
        base = [selected, ...base];
      }
      return { options: base, hasMatches: true };
    }
    const filtered = source.filter((emp) => (emp.searchIndex || buildEmployeeSearchIndex(emp)).includes(term));
    let limited = filtered.slice(0, MAX_EMPLOYEE_OPTIONS);
    if (selected && !limited.some((emp) => String(emp.id) === selectedId)) {
      limited = [selected, ...limited];
    }
    return { options: limited, hasMatches: filtered.length > 0 };
  }, [employees, employeeSearchMap]);

  const handleEmployeeInputChange = useCallback((row, value) => {
    if (isLocked) return;
    const nextValue = String(value ?? '');
    setEmployeeSearchMap((prev) => {
      const hasCustom = Object.prototype.hasOwnProperty.call(prev, row.localId);
      const current = hasCustom ? prev[row.localId] : row.fullName || '';
      if (current === nextValue) return prev;
      return { ...prev, [row.localId]: nextValue };
    });
    const selected = row.employeeId ? employees.find((emp) => String(emp.id) === String(row.employeeId)) : null;
    if (!selected) {
      updateRow(row.localId, { fullName: nextValue });
      return;
    }
    const normalizedSelected = String(selected.fullName || '').trim();
    if (!nextValue.trim()) {
      updateRow(row.localId, { employeeId: null, fullName: '' });
      return;
    }
    if (nextValue.trim() !== normalizedSelected) {
      updateRow(row.localId, { employeeId: null, fullName: nextValue });
      return;
    }
    updateRow(row.localId, { fullName: nextValue });
  }, [employees, isLocked, updateRow]);

  const handleEmployeeSearchFocus = useCallback((rowId) => {
    if (isLocked) return;
    if (employeeDropdownTimerRef.current) clearTimeout(employeeDropdownTimerRef.current);
    setActiveEmployeeDropdown(rowId);
  }, [isLocked]);

  const handleEmployeeSearchBlur = useCallback(() => {
    if (employeeDropdownTimerRef.current) clearTimeout(employeeDropdownTimerRef.current);
    employeeDropdownTimerRef.current = setTimeout(() => setActiveEmployeeDropdown(null), 150);
  }, []);

  useEffect(() => {
    if (isLocked && activeEmployeeDropdown) {
      setActiveEmployeeDropdown(null);
    }
  }, [isLocked, activeEmployeeDropdown]);

  const handleEmployeeOptionPick = (row, employee) => {
    if (!employee) return;
    handleEmployeeSelect(row, employee.id);
  };

  const handleManagerSelect = (value) => {
    if (isLocked) return;
    const nextId = value ? String(value) : '';
    const info = managers.find((m) => String(m.id) === nextId);
    setForm((prev) => ({
      ...prev,
      managerUserId: nextId || null,
      ...(prev.registerType === 'manager'
        ? {
            objectName: info ? info.name : nextId ? prev.objectName : '',
            objectCode: info ? info.id : nextId ? prev.objectCode : '',
          }
        : {}),
    }));
    managerAutoKeyRef.current = '';
    setManagerInfo(info || null);
    setManagerPreview(null);
    setManagerPreviewError('');
    markDirty();
  };

  const fetchManagerPreview = useCallback(async ({ applyRows = false } = {}) => {
    if (form.registerType !== 'manager' || isLocked) return null;
    if (!form.managerUserId) {
      setManagerPreviewError('Выберите менеджера');
      return null;
    }
    setManagerPreviewLoading(true);
    setManagerPreviewError('');
    try {
      const params = new URLSearchParams();
      params.set('managerUserId', form.managerUserId);
      params.set('month', Number(form.periodMonth) || now.getMonth() + 1);
      params.set('year', Number(form.periodYear) || now.getFullYear());
      const resp = await fetch(`${API_BASE}/api/payrolls/manager/preview?${params.toString()}`, { headers: { ...authHeaders } });
      const data = await resp.json();
      if (!resp.ok) {
        if (resp.status === 404 && data?.error === 'manager_not_found') {
          throw new Error('Менеджер не найден или не имеет назначений');
        }
        throw new Error('Не удалось получить данные по менеджеру');
      }
      setManagerPreview(data);
      if (data.manager) setManagerInfo(data.manager);
      if (applyRows) {
        const previewRows = Array.isArray(data.rows) ? data.rows : [];
        setForm((prev) => ({
          ...prev,
          rows: ensureExtraBlankRow(previewRows.map((row) => mapRowFromApi(row))),
        }));
        markDirty();
      }
      return data;
    } catch (e) {
      console.error('manager preview error', e);
      setManagerPreview(null);
      setManagerPreviewError(e?.message || 'Не удалось получить данные по менеджеру');
      return null;
    } finally {
      setManagerPreviewLoading(false);
    }
  }, [authHeaders, form.registerType, form.managerUserId, form.periodMonth, form.periodYear, ensureExtraBlankRow, isLocked, markDirty]);

  useEffect(() => {
    if (form.registerType !== 'manager') return;
    if (!form.id) return;
    if (!hasFilledRows) return;
    managerAutoKeyRef.current = `${form.managerUserId || ''}-${form.periodMonth}-${form.periodYear}`;
  }, [form.registerType, form.id, form.managerUserId, form.periodMonth, form.periodYear, hasFilledRows]);

  useEffect(() => {
    if (form.registerType !== 'manager' || isLocked) return;
    if (!form.managerUserId) return;
    const key = `${form.managerUserId}-${form.periodMonth}-${form.periodYear}`;
    const shouldAutofill = !hasFilledRows || managerAutoKeyRef.current !== key;
    if (!shouldAutofill) return;
    let cancelled = false;
    (async () => {
      const data = await fetchManagerPreview({ applyRows: true });
      if (!cancelled && data) {
        managerAutoKeyRef.current = key;
      }
    })();
    return () => { cancelled = true; };
  }, [form.registerType, form.managerUserId, form.periodMonth, form.periodYear, hasFilledRows, fetchManagerPreview, isLocked]);

  const handleManagerPreviewRefresh = () => {
    if (isLocked || form.registerType !== 'manager') return;
    fetchManagerPreview({ applyRows: false });
  };

  const handleManagerAutofill = async () => {
    if (isLocked || managerPreviewLoading) return;
    if (form.registerType !== 'manager') return;
    if (!form.managerUserId) {
      alert('Сначала выберите менеджера.');
      return;
    }
    const hasExisting = (form.rows || []).some((row) => String(row.fullName || '').trim());
    if (hasExisting && !window.confirm('Текущие строки будут заменены автоматическим расчётом. Продолжить?')) {
      return;
    }
    const data = await fetchManagerPreview({ applyRows: true });
    if (data) {
      managerAutoKeyRef.current = `${form.managerUserId}-${form.periodMonth}-${form.periodYear}`;
    }
  };

  const handleCopyPreviousMonth = useCallback(async () => {
    if (isLocked || form.registerType === 'manager') return;
    if (!form.cardId) {
      alert('Выберите актуальный объект для ведомости.');
      return;
    }
    if (!form.periodMonth || !form.periodYear) {
      alert('Укажите месяц и год ведомости.');
      return;
    }
    if (hasFilledRows && !window.confirm('Текущие строки будут заменены данными из прошлой ведомости. Продолжить?')) {
      return;
    }
    setCopyingPrevious(true);
    try {
      const params = new URLSearchParams({
        cardId: String(form.cardId),
        month: String(form.periodMonth),
        year: String(form.periodYear),
      });
      const resp = await fetch(`${API_BASE}/api/payrolls/previous?${params.toString()}`, { headers: { ...authHeaders } });
      const data = await resp.json();
      if (!resp.ok) {
        if (resp.status === 404) {
          alert('Не нашли ведомость за прошлый месяц для этого объекта.');
          return;
        }
        throw new Error(data?.error || 'copy_failed');
      }
      const mappedRows = ensureExtraBlankRow((Array.isArray(data.rows) ? data.rows : []).map((row) => mapRowFromApi(row)));
      setForm((prev) => ({
        ...prev,
        title: data.title ?? prev.title,
        serviceName: data.serviceName ?? prev.serviceName,
        unitName: data.unitName ?? prev.unitName,
        notes: data.notes ?? prev.notes,
        rows: mappedRows,
      }));
      setStatusMessage('Данные скопированы из прошлого месяца');
      setTimeout(() => setStatusMessage(''), 3000);
      markDirty();
    } catch (e) {
      console.error('copy previous payroll error', e);
      alert('Не удалось скопировать ведомость.');
    } finally {
      setCopyingPrevious(false);
    }
  }, [authHeaders, ensureExtraBlankRow, form.cardId, form.periodMonth, form.periodYear, form.registerType, hasFilledRows, isLocked, markDirty]);

  const removeRow = (rowId) => {
    if (isLocked) return;
    setForm((prev) => {
      const filtered = prev.rows.filter((row) => row.localId !== rowId);
      const ensured = ensureExtraBlankRow(filtered.length ? filtered : [blankRow()]);
      return { ...prev, rows: ensured };
    });
    markDirty();
  };

  const handleEmployeeSelect = (row, employeeId) => {
    if (isLocked) return;
    if (!employeeId) {
      updateRow(row.localId, { employeeId: null, fullName: '', position: '' });
      setEmployeeSearchMap((prev) => {
        if (!Object.prototype.hasOwnProperty.call(prev, row.localId)) return prev;
        const next = { ...prev };
        delete next[row.localId];
        return next;
      });
      setActiveEmployeeDropdown((prev) => (prev === row.localId ? null : prev));
      return;
    }
    const employee = employees.find((e) => String(e.id) === String(employeeId));
    if (!employee) return;
    updateRow(row.localId, { employeeId: employee.id, fullName: employee.fullName, position: employee.position || '' });
    setEmployeeSearchMap((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, row.localId)) return prev;
      const next = { ...prev };
      delete next[row.localId];
      return next;
    });
    setActiveEmployeeDropdown((prev) => (prev === row.localId ? null : prev));
  };

  const handleManualPosition = (row, positionId) => {
    if (isLocked) return;
    const pos = positions.find((p) => String(p.id) === String(positionId));
    updateRow(row.localId, { position: pos ? pos.name : '' });
  };

  const payloadFromForm = useCallback(() => {
    const rows = (form.rows || [])
      .map((row, index) => ({
        rowOrder: index,
        rowId: row.id || null,
        employeeId: row.employeeId || null,
        fullName: String(row.fullName || '').trim(),
        position: String(row.position || '').trim() || null,
        paidToCard: parseNumber(row.paidToCard),
        advanceAmount: parseNumber(row.advanceAmount),
        incomeAmount: parseNumber(row.incomeAmount),
        toPayAmount: calcToPayAmount(row),
      }))
      .filter((row) => row.fullName);
    return {
      title: form.title,
      serviceName: form.serviceName,
      unitName: form.unitName,
      objectName: form.objectName,
      objectCode: form.objectCode,
      periodMonth: Number(form.periodMonth) || (now.getMonth() + 1),
      periodYear: Number(form.periodYear) || now.getFullYear(),
      notes: form.notes,
      registerType: form.registerType,
      cardId: form.cardId,
      managerUserId: form.managerUserId,
      rows,
    };
  }, [form]);

  const mergeServerMeta = useCallback((data) => {
    if (!data) return;
    setForm((prev) => ({
      ...prev,
      status: data.status || prev.status,
      signedBy: data.signedBy ?? prev.signedBy,
      signedAt: data.signedAt ?? prev.signedAt,
      issuedBy: data.issuedBy ?? prev.issuedBy,
      issuedAt: data.issuedAt ?? prev.issuedAt,
    }));
  }, []);

  const saveRegister = useCallback(async ({ silent = false } = {}) => {
    const rowsFilled = (form.rows || []).filter((row) => String(row.fullName || '').trim());
    if (!rowsFilled.length) {
      const message = 'Добавьте хотя бы одну строку с ФИО.';
      if (silent) {
        setAutoSaveState('blocked');
        setAutoSaveError(message);
      } else {
        alert(message);
      }
      return false;
    }
    if (form.registerType === 'manager') {
      if (!form.managerUserId) {
        const message = 'Выберите менеджера, чтобы сохранить ведомость.';
        if (silent) {
          setAutoSaveState('blocked');
          setAutoSaveError(message);
        } else {
          alert(message);
        }
        return false;
      }
    } else if (!form.cardId) {
      const message = 'Выберите актуальный объект из списка.';
      if (silent) {
        setAutoSaveState('blocked');
        setAutoSaveError(message);
      } else {
        alert(message);
      }
      return false;
    }
    if (silent) {
      setAutoSaveState('saving');
      setAutoSaveError('');
    } else {
      setSaving(true);
      setStatusMessage('');
    }
    try {
      const payload = payloadFromForm();
      const method = form.id ? 'PUT' : 'POST';
      const url = form.id ? `${API_BASE}/api/payrolls/${form.id}` : `${API_BASE}/api/payrolls`;
      const resp = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(payload),
      });
      const data = await resp.json();
      if (!resp.ok) {
        const apiError = data?.error || 'save_failed';
        const showLimitError = () => {
          const limit = Number((data?.fotLimit ?? cardInfo?.fotWithoutManager ?? currentFotLimit) || 0);
          const total = Number((data?.total ?? totals.toPayAmount) || 0);
          const issued = Number(data?.limit?.usedIssued || 0);
          const message = `Сумма к выплате ${currency(total)} ₽ вместе с уже выданными ${currency(issued)} ₽ превышает лимит ${currency(limit)} ₽ по ФОТ.`;
          if (silent) {
            setAutoSaveState('blocked');
            setAutoSaveError(message);
          } else {
            alert(message);
          }
        };
        if (apiError === 'limit_exceeded') {
          showLimitError();
          return false;
        }
        if (apiError === 'card_not_actual') {
          const message = 'Карточка объекта больше не актуальна. Выберите свежую карточку или актуализируйте расчёт.';
          if (silent) {
            setAutoSaveState('blocked');
            setAutoSaveError(message);
          } else {
            alert(message);
          }
          return false;
        }
        if (apiError === 'card_period_mismatch') {
          const message = 'Выбранная карточка расчёта относится к другому периоду. Уточните месяц/год или выберите подходящий объект.';
          if (silent) {
            setAutoSaveState('blocked');
            setAutoSaveError(message);
          } else {
            alert(message);
          }
          return false;
        }
        throw new Error(apiError);
      }
      const localIdByServerId = new Map();
      (form.rows || []).forEach((row) => {
        if (row.id) {
          localIdByServerId.set(row.id, row.localId);
        }
      });
      const mappedRows = (data.rows || []).map((row) => {
        const mapped = mapRowFromApi(row);
        if (mapped.id && localIdByServerId.has(mapped.id)) {
          mapped.localId = localIdByServerId.get(mapped.id);
        }
        return mapped;
      });
      setForm({
        id: data.id,
        title: data.title,
        serviceName: data.serviceName,
        unitName: data.unitName,
        objectName: data.objectName,
        objectCode: data.objectCode,
        periodMonth: data.periodMonth,
        periodYear: data.periodYear,
        notes: data.notes || '',
        registerType: data.registerType || form.registerType,
        cardId: data.cardId || form.cardId,
        managerUserId: data.managerUserId || form.managerUserId,
        fotLimit: data.fotLimit || 0,
        status: data.status || form.status,
        signedBy: data.signedBy || null,
        signedAt: data.signedAt || null,
        issuedBy: data.issuedBy || null,
        issuedAt: data.issuedAt || null,
        rows: mappedRows.concat([blankRow()]),
      });
      setCardInfo(data.cardInfo ? ensureObjectSearchMeta(data.cardInfo) : cardInfo);
      setManagerInfo(data.managerInfo || managerInfo);
      if (data.limit) {
        setLimitUsage(normalizeLimitUsage(data.limit));
      }
      if (!silent) {
        setStatusMessage('Сохранено');
        setTimeout(() => setStatusMessage(''), 3000);
      }
      setAutoSaveState('saved');
      setAutoSaveError('');
      lastSavedKeyRef.current = dirtyKey;
      resetPendingRows();
      if (!form.id && data.id) {
        navigate(`/payrolls/${data.id}`, { replace: true });
      }
      return true;
    } catch (e) {
      console.error('save payroll error', e);
      if (!silent) {
        alert('Не удалось сохранить ведомость.');
      }
      setAutoSaveState('error');
      setAutoSaveError(e?.message || 'Ошибка сохранения');
      return false;
    } finally {
      if (!silent) setSaving(false);
    }
  }, [authHeaders, cardInfo, dirtyKey, form, navigate, payloadFromForm, managerInfo, currentFotLimit, totals.toPayAmount, resetPendingRows]);

  useEffect(() => {
    saveRef.current = saveRegister;
  }, [saveRegister]);

  useEffect(() => {
    if (!canAutoSave) {
      if (!hasLinkedEntity || !hasFilledRows) {
        setAutoSaveState('idle');
        setAutoSaveError('');
      }
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      return undefined;
    }
    if (dirtyKey === 0) return undefined;
    if (dirtyKey === lastSavedKeyRef.current) return undefined;
    setAutoSaveState('pending');
    setAutoSaveError('');
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      saveRef.current?.({ silent: true });
    }, AUTO_SAVE_DELAY);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [dirtyKey, canAutoSave, hasLinkedEntity, hasFilledRows]);

  const handleDelete = async () => {
    if (!form.id) return;
    if (isLocked) {
      alert('Удаление доступно только для черновиков.');
      return;
    }
    if (!window.confirm('Удалить ведомость без возможности восстановления?')) return;
    try {
      await fetch(`${API_BASE}/api/payrolls/${form.id}`, { method: 'DELETE', headers: { ...authHeaders } });
      setStatusMessage('Удалено');
      navigate('/payrolls', { replace: true });
    } catch (e) {
      alert('Ошибка удаления');
    }
  };

  const fetchManagerPreviewSnapshot = useCallback(async () => {
    if (form.registerType !== 'manager' || !form.managerUserId) return null;
    const params = new URLSearchParams();
    params.set('managerUserId', form.managerUserId);
    params.set('month', Number(form.periodMonth) || (now.getMonth() + 1));
    params.set('year', Number(form.periodYear) || now.getFullYear());
    const resp = await fetch(`${API_BASE}/api/payrolls/manager/preview?${params.toString()}`, { headers: { ...authHeaders } });
    const data = await resp.json();
    if (!resp.ok) throw new Error(data?.error || 'preview_failed');
    return data;
  }, [authHeaders, form.managerUserId, form.periodMonth, form.periodYear, form.registerType]);

  const fetchRegistersSummary = useCallback(async () => {
    const params = new URLSearchParams();
    params.set('month', Number(form.periodMonth) || (now.getMonth() + 1));
    params.set('year', Number(form.periodYear) || now.getFullYear());
    const resp = await fetch(`${API_BASE}/api/payrolls?${params.toString()}`, { headers: { ...authHeaders } });
    const data = await resp.json();
    if (!resp.ok) throw new Error(data?.error || 'registers_failed');
    return Array.isArray(data.items) ? data.items : [];
  }, [authHeaders, form.periodMonth, form.periodYear]);

  const handlePrint = async (mode) => {
    const rows = (form.rows || []).filter((row) => String(row.fullName || '').trim());
    if (!rows.length) {
      alert('Нет строк для печати.');
      return;
    }
    const sheetForPrint = { ...form, fotLimit: currentFotLimit, staffAdvancesComment: staffAdvancesRaw };
    if (isManagerMode) {
      if (mode === 'manager-only') {
        const html = buildPrintDocument(sheetForPrint, rows, 'full');
        openPrintWindow(html);
        return;
      }
      if (mode === 'manager-with-objects') {
        try {
          let previewError = null;
          let previewData = managerPreviewFresh;
          if (!previewData) {
            try {
              previewData = await fetchManagerPreviewSnapshot();
            } catch (err) {
              console.warn('manager preview fetch failed', err);
              previewError = err;
            }
          }
          let summaryError = null;
          let summaryItems = [];
          try {
            summaryItems = await fetchRegistersSummary();
          } catch (err) {
            console.warn('manager objects summary fetch failed', err);
            summaryError = err;
          }
          const relatedCardIds = new Set(
            (previewData?.objects || [])
              .map((obj) => Number(obj.cardId) || 0)
              .filter((id) => id > 0)
          );
          let objectRegisters = (summaryItems || [])
            .filter((item) => (
              item &&
              item.registerType !== 'manager' &&
              relatedCardIds.has(Number(item.cardId) || 0) &&
              ['formed', 'issued'].includes(String(item.status || '').toLowerCase())
            ))
            .map((item) => ({
              objectName: item.objectName,
              objectCode: item.objectCode,
              totalPaidCard: item.totalPaidCard,
              totalAdvance: item.totalAdvance,
              totalIncome: item.totalIncome,
              totalToPay: item.totalToPay,
            }))
            .sort((a, b) => String(a.objectName || '').localeCompare(String(b.objectName || '')));
          if (!objectRegisters.length) {
            const previewObjects = Array.isArray(previewData?.objects) ? previewData.objects : [];
            objectRegisters = previewObjects.map((obj) => {
              const paidCard = round2(Number(obj.cardShare) || 0);
              const income = round2(Number(obj.totalShare) || 0);
              const advance = round2(income - paidCard);
              const toPay = round2(Number(obj.cashShare) || advance);
              return {
                objectName: obj.objectName || `Объект ${obj.objectId ?? ''}`,
                objectCode: obj.objectCode || '',
                totalPaidCard: paidCard,
                totalAdvance: advance,
                totalIncome: income,
                totalToPay: toPay,
              };
            });
          }
          if (!objectRegisters.length) {
            objectRegisters = rows.map((row, idx) => ({
              objectName: row.fullName || `Объект ${idx + 1}`,
              objectCode: row.position || '',
              totalPaidCard: round2(parseNumber(row.paidToCard)),
              totalAdvance: round2(parseNumber(row.advanceAmount)),
              totalIncome: round2(parseNumber(row.incomeAmount)),
              totalToPay: round2(calcToPayAmount(row)),
            }));
          }
          if (!objectRegisters.length) {
            const details = summaryError?.message || summaryError?.toString() || '';
            const message = summaryError
              ? `Не удалось собрать данные по объектам менеджера для печати. ${details}`.trim()
              : previewError
                ? 'Не удалось получить список объектов менеджера для печати.'
                : 'Нет данных по объектам менеджера за выбранный месяц.';
            alert(message);
            return;
          }
          const html = buildManagerWithObjectsDocument({ ...form, fotLimit: currentFotLimit }, rows, previewData || undefined, objectRegisters);
          openPrintWindow(html);
        } catch (e) {
          console.error('manager full print error', e);
          const details = e?.message ? `\n${e.message}` : '';
          alert(`Не удалось собрать данные по объектам менеджера для печати.${details}`);
        }
        return;
      }
    }
    const html = buildPrintDocument(sheetForPrint, rows, mode);
    openPrintWindow(html);
  };

  const handleSign = async () => {
    if (!form.id || form.status !== 'draft') return;
    if (form.registerType !== 'manager' && limitInfo.exceeds) {
      alert('Подписать нельзя: сумма ведомости превышает доступный лимит ФОТ.');
      return;
    }
    if (!window.confirm('Подписать ведомость и запретить дальнейшее редактирование?')) return;
    setSigning(true);
    try {
      const resp = await fetch(`${API_BASE}/api/payrolls/${form.id}/sign`, { method: 'POST', headers: { ...authHeaders } });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.error || 'sign_failed');
      mergeServerMeta(data);
      if (data.limit) {
        setLimitUsage(normalizeLimitUsage(data.limit));
      }
      setStatusMessage('Ведомость подписана');
    } catch (e) {
      console.error('sign payroll error', e);
      alert('Не удалось подписать ведомость.');
    } finally {
      setSigning(false);
      setTimeout(() => setStatusMessage(''), 3000);
    }
  };

  const handleIssue = async () => {
    if (!form.id || form.status !== 'formed') return;
    if (!window.confirm('Подтвердить выдачу ведомости?')) return;
    setIssuing(true);
    try {
      const resp = await fetch(`${API_BASE}/api/payrolls/${form.id}/issue`, { method: 'POST', headers: { ...authHeaders } });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.error || 'issue_failed');
      mergeServerMeta(data);
      if (data.limit) {
        setLimitUsage(normalizeLimitUsage(data.limit));
      }
      setStatusMessage('Ведомость выдана');
    } catch (e) {
      console.error('issue payroll error', e);
      alert('Не удалось изменить статус ведомости.');
    } finally {
      setIssuing(false);
      setTimeout(() => setStatusMessage(''), 3000);
    }
  };

  const handleUnlock = async () => {
    if (!form.id || form.status === 'draft') return;
    if (!window.confirm('Снять подписи и вернуть ведомость в черновик?')) return;
    setUnlocking(true);
    try {
      const resp = await fetch(`${API_BASE}/api/payrolls/${form.id}/unlock`, { method: 'POST', headers: { ...authHeaders } });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.error || 'unlock_failed');
      mergeServerMeta(data);
      if (data.limit) {
        setLimitUsage(normalizeLimitUsage(data.limit));
      }
      setStatusMessage('Подпись снята');
    } catch (e) {
      console.error('unlock payroll error', e);
      alert('Не удалось снять подписи.');
    } finally {
      setUnlocking(false);
      setTimeout(() => setStatusMessage(''), 3000);
    }
  };

  const registerTitle = form.id ? `Ведомость #${form.id}` : 'Новая ведомость';
  const periodLabel = `${monthNames[(Number(form.periodMonth) || 1) - 1]} ${form.periodYear || now.getFullYear()}`;
  const typeBadge = registerTypeBadges[form.registerType || 'standard'];
  const isManagerMode = form.registerType === 'manager';
  const managerDisplayName = selectedManager?.name || managerInfo?.name || '';
  const managerDisplayId = selectedManager?.id || managerInfo?.id || form.managerUserId || '';
  const objectFieldLabel = isManagerMode ? 'Менеджер' : 'Объект';
  const codeFieldLabel = isManagerMode ? 'ID менеджера' : 'Код/№ объекта';
  const headerContext = form.registerType === 'manager'
    ? (selectedManager ? `Менеджер: ${selectedManager.name} · ${selectedManager.id || form.managerUserId || '—'}` : 'Менеджер не выбран')
    : (form.objectName ? `${form.objectName} · ${form.objectCode || '—'}` : 'Объект не выбран');
  const cardLinkTarget = cardInfo?.objectCode || form.objectCode || '';
  const cardLinkLabel = cardInfo?.objectCodeFull || cardInfo?.objectCode || form.objectCode || '';
  const staffAdvancesRaw = cardInfo?.staffAdvancesComment || '';
  const hasStaffAdvancesComment = staffAdvancesRaw.trim().length > 0;
  const renderNotesField = (wrapperStyle = {}) => (
    <div style={wrapperStyle}>
      <div style={labelStyle}>Примечание</div>
      <textarea
        value={form.notes}
        onChange={(e) => updateField('notes', e.target.value)}
        style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }}
        disabled={isLocked}
      />
    </div>
  );

  return (
    <div style={pageWrapper}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 20 }}>
        <button onClick={() => navigate('/payrolls')} style={{ padding: '8px 14px', borderRadius: 10, border: '1px solid #cbd5f5', background: '#f8fafc', cursor: 'pointer' }}>← К журналу</button>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{registerTitle}</div>
          <div style={{ fontSize: 13, color: '#64748b' }}>Период: {periodLabel}</div>
        </div>
        <span style={{ padding: '4px 12px', borderRadius: 999, fontWeight: 600, background: typeBadge.bg, color: typeBadge.color }}>
          {registerTypeLabels[form.registerType || 'standard']}
        </span>
        <span style={{ padding: '4px 12px', borderRadius: 999, fontWeight: 600, background: statusBadgeStyle.bg, color: statusBadgeStyle.color }}>
          {currentStatusLabel}
        </span>
        <div style={{ fontSize: 12, color: autoSaveInfo.color, fontWeight: 600 }}>Автосохранение: {autoSaveInfo.text}</div>
        <div style={{ fontSize: 13, color: '#475569' }}>{headerContext}</div>
        {(form.signedAt || form.issuedAt) && (
          <div style={{ fontSize: 12, color: '#475569' }}>
            {form.signedAt && (
              <span>Подписал: {form.signedBy || '—'} · {formatDateTime(form.signedAt)}</span>
            )}
            {form.issuedAt && (
              <span>{form.signedAt ? ' · ' : ''}Выдано: {form.issuedBy || '—'} · {formatDateTime(form.issuedAt)}</span>
            )}
          </div>
        )}
      </div>

      <div style={{ ...panel, padding: 0 }}>
          <div style={{ padding: '16px 16px 0 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
            <div style={{ fontSize: 18, fontWeight: 600 }}>Редактор ведомости</div>
            <span style={{ padding: '4px 12px', borderRadius: 999, fontWeight: 600, background: registerTypeBadges[form.registerType || 'standard'].bg, color: registerTypeBadges[form.registerType || 'standard'].color }}>
              {registerTypeLabels[form.registerType || 'standard']}
            </span>
          </div>
          <div style={{ padding: '16px 16px 0 16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
            <div>
              <div style={labelStyle}>Название</div>
              <input value={form.title} onChange={(e) => updateField('title', e.target.value)} style={inputStyle} disabled={isLocked} />
            </div>
            <div>
              <div style={labelStyle}>Подразделение</div>
              <input value={form.unitName} onChange={(e) => updateField('unitName', e.target.value)} style={inputStyle} disabled={isLocked} />
            </div>
            <div>
              <div style={labelStyle}>{objectFieldLabel}</div>
              <input
                value={form.registerType === 'manager' ? managerDisplayName : form.objectName}
                readOnly
                style={{ ...inputStyle, background: '#f8fafc' }}
              />
            </div>
            <div>
              <div style={labelStyle}>{codeFieldLabel}</div>
              <input
                value={form.registerType === 'manager' ? managerDisplayId : (form.objectCode || '')}
                readOnly
                style={{ ...inputStyle, background: '#f8fafc' }}
              />
            </div>
            <div>
              <div style={labelStyle}>Месяц</div>
              <select value={form.periodMonth} onChange={(e) => updateField('periodMonth', Number(e.target.value))} style={selectStyle} disabled={isLocked}>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <option key={m} value={m}>{monthNames[m - 1]}</option>
                ))}
              </select>
            </div>
            <div>
              <div style={labelStyle}>Год</div>
              <input type="number" value={form.periodYear} onChange={(e) => updateField('periodYear', Number(e.target.value))} style={inputStyle} disabled={isLocked} />
            </div>
          </div>

          <div style={{ padding: '0 16px 16px' }}>
            {form.registerType === 'manager' ? (
              <>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
                  <div style={{ flex: '1 1 280px' }}>
                    <div style={labelStyle}>Менеджер</div>
                    <select
                      value={form.managerUserId || ''}
                      onChange={(e) => handleManagerSelect(e.target.value)}
                      style={{ ...selectStyle, background: '#fff' }}
                      disabled={isLocked}
                    >
                      <option value="">Выберите менеджера…</option>
                      {managerOptions.map((manager) => (
                        <option key={manager.id} value={manager.id}>{manager.id} · {manager.name}</option>
                      ))}
                    </select>
                    {managersLoading && <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>Загрузка пользователей…</div>}
                    {managerError && <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 4 }}>{managerError}</div>}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    <button
                      onClick={handleManagerPreviewRefresh}
                      disabled={isLocked || !form.managerUserId || managerPreviewLoading}
                      style={{
                        padding: '10px 16px',
                        borderRadius: 10,
                        border: '1px solid #0ea5e9',
                        background: isLocked ? '#e2e8f0' : '#e0f2fe',
                        color: isLocked ? '#94a3b8' : '#0369a1',
                      }}
                    >
                      {managerPreviewLoading ? 'Считаем…' : 'Рассчитать' }
                    </button>
                    <button
                      onClick={handleManagerAutofill}
                      disabled={isLocked || !form.managerUserId || managerPreviewLoading}
                      style={{
                        padding: '10px 16px',
                        borderRadius: 10,
                        border: '1px solid #16a34a',
                        background: isLocked ? '#d1fae5' : '#bbf7d0',
                        color: '#166534',
                      }}
                    >
                      Заполнить строки
                    </button>
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 16 }}>
                  {renderNotesField({ flex: '1 1 320px', minWidth: 260 })}
                </div>
                {managerPreviewLoading && <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>Собираем данные по объектам…</div>}
                {managerPreviewError && <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 6 }}>{managerPreviewError}</div>}
                {managerPreview && (
                  <div style={{ marginTop: 14, padding: 16, borderRadius: 12, border: '1px solid #e2e8f0', background: '#f8fafc' }}>
                    <div style={{ fontWeight: 600, marginBottom: 12 }}>Автосбор по менеджеру — {managerPreviewTotals.count} объектов</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, fontSize: 13 }}>
                      <div>
                        <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Итого доход</div>
                        <div style={{ fontWeight: 700 }}>{currency(managerPreviewTotals.income || 0)} ₽</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Карта</div>
                        <div style={{ fontWeight: 600 }}>{currency(managerPreviewTotals.card || 0)} ₽</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Наличные</div>
                        <div style={{ fontWeight: 600 }}>{currency(managerPreviewTotals.cash || 0)} ₽</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Период</div>
                        <div style={{ fontWeight: 600 }}>{managerPreview.period?.start || '—'} — {managerPreview.period?.end || '—'}</div>
                      </div>
                    </div>
                    {Array.isArray(managerPreview.objects) && managerPreview.objects.length > 0 && (
                      <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {managerPreview.objects.slice(0, 6).map((obj) => (
                          <div key={`${obj.objectId}-${obj.objectCode}`} style={{ padding: 12, borderRadius: 10, background: '#fff', border: '1px solid #e2e8f0' }}>
                            <div style={{ fontWeight: 600 }}>{obj.objectName || 'Объект'} · {obj.objectCode || '—'}</div>
                            <div style={{ fontSize: 12, color: '#475569' }}>Доход: {currency(obj.totalShare)} ₽ · Карта: {currency(obj.cardShare)} ₽ · Нал: {currency(obj.cashShare)} ₽</div>
                            {!!obj.assignments?.length && (
                              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                                {obj.assignments.map((ass, idx) => `${ass.slot ? `Слот ${ass.slot}` : 'Смена'} — ${ass.days} дн (${ass.ratio}%)`).join('; ')}
                              </div>
                            )}
                          </div>
                        ))}
                        {managerPreview.objects.length > 6 && (
                          <div style={{ fontSize: 12, color: '#475569' }}>…и ещё {managerPreview.objects.length - 6} объектов</div>
                        )}
                      </div>
                    )}
                    {Array.isArray(managerPreview.skipped) && managerPreview.skipped.length > 0 && (
                      <div style={{ marginTop: 14, fontSize: 12, color: '#b45309' }}>
                        <div style={{ fontWeight: 600 }}>Пропущены объекты ({managerPreview.skipped.length}):</div>
                        <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
                          {managerPreview.skipped.slice(0, 5).map((skip) => (
                            <li key={`${skip.objectId}-${skip.objectCode || ''}`}>
                              {skip.objectName || `Объект ${skip.objectId}`} — {(Array.isArray(skip.reasons) ? skip.reasons : []).map((reason) => managerSkipReasonText[reason] || reason).join(', ')}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                  <div style={{ flex: '1 1 320px', minWidth: 260 }}>
                    <div style={labelStyle}>Актуальный объект</div>
                    <input
                      type="search"
                      value={objectSearchTerm}
                      onChange={(e) => setObjectSearchTerm(e.target.value)}
                      placeholder="Поиск по названию, адресу, коду…"
                      style={{ ...inputStyle, marginBottom: 6 }}
                      disabled={isLocked}
                    />
                    <select value={form.cardId || ''} onChange={(e) => handleObjectSelect(e.target.value)} style={{ ...selectStyle, background: '#fff' }} disabled={isLocked}>
                      <option value="">Выберите объект за период…</option>
                      {objectSelectData.options.map((obj) => (
                        <option key={obj.cardId} value={obj.cardId}>
                          {obj.objectName} · {obj.objectCodeFull || obj.objectCode}
                        </option>
                      ))}
                    </select>
                    {normalizedObjectQuery && !objectSelectData.hasMatches && (
                      <div style={{ fontSize: 12, color: '#b45309', marginTop: 4 }}>Нет совпадений. Попробуйте изменить запрос.</div>
                    )}
                    {objectsLoading && <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>Загрузка объектов…</div>}
                    {objectError && <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 4 }}>{objectError}</div>}
                    {form.cardId && !objectsLoading && (
                      <div
                        style={{
                          marginTop: 4,
                          fontSize: 12,
                          color: '#475569',
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 8,
                          alignItems: 'center',
                        }}
                      >
                        <span>Код: {cardLinkLabel || '—'}</span>
                        {cardLinkTarget && (
                          <a
                            href={`/card-calc/${encodeURIComponent(cardLinkTarget)}`}
                            target="_blank"
                            rel="noreferrer noopener"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              padding: '4px 10px',
                              borderRadius: 999,
                              border: '1px solid #cbd5f5',
                              background: '#f8fafc',
                              textDecoration: 'none',
                              color: '#1d4ed8',
                              fontWeight: 600,
                            }}
                          >
                            Открыть карточку
                            <span aria-hidden="true" style={{ fontSize: 14 }}>↗</span>
                          </a>
                        )}
                      </div>
                    )}
                    <div
                      style={{
                        marginTop: 12,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 8,
                        alignItems: 'center',
                      }}
                    >
                      <button
                        type="button"
                        onClick={handleCopyPreviousMonth}
                        disabled={isLocked || !form.cardId || copyingPrevious}
                        style={{
                          padding: '10px 14px',
                          borderRadius: 10,
                          border: '1px solid #0f766e',
                          background: isLocked || !form.cardId ? '#e2e8f0' : '#d1fae5',
                          color: isLocked || !form.cardId ? '#94a3b8' : '#065f46',
                          fontWeight: 600,
                          cursor: isLocked || !form.cardId ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {copyingPrevious ? 'Копируем…' : 'Скопировать с прошлого месяца'}
                      </button>
                      <span style={{ fontSize: 12, color: '#475569' }}>Перезапишет строки текущей ведомости</span>
                    </div>
                  </div>
                  {renderNotesField({ flex: '1 1 320px', minWidth: 260 })}
                  <div style={{ flex: '1 1 320px', minWidth: 260 }}>
                    <div style={labelStyle}>Авансы (из карточки расчёта)</div>
                    <div
                      style={{
                        ...inputStyle,
                        minHeight: 92,
                        background: '#f8fafc',
                        borderStyle: 'dashed',
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.4,
                        color: cardInfo && form.cardId && hasStaffAdvancesComment ? '#0f172a' : '#94a3b8',
                      }}
                    >
                      {cardInfo && form.cardId
                        ? (hasStaffAdvancesComment ? staffAdvancesRaw : 'Комментариев нет')
                        : 'Выберите объект, чтобы увидеть комментарий'}
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    marginTop: 16,
                    padding: 16,
                    borderRadius: 12,
                    background: limitBlockedByFot ? '#fef2f2' : limitWarnByLocked ? '#fff7ed' : '#f8fafc',
                    border: `1px solid ${limitBlockedByFot ? '#fecaca' : limitWarnByLocked ? '#fed7aa' : '#e2e8f0'}`,
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>Контроль лимита ФОТ за месяц</div>
                  {!form.cardId ? (
                    <div style={{ fontSize: 13, color: '#475569' }}>Выберите объект, чтобы увидеть лимит и учтённые выплаты за месяц.</div>
                  ) : (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, fontSize: 13 }}>
                        <div>
                          <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Лимит ФОТ (без менеджера)</div>
                          <div style={{ fontWeight: 700 }}>{currency(limitInfo.fotLimit)} ₽</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Уже выдано по другим ведомостям</div>
                          <div style={{ fontWeight: 600 }}>{currency(limitInfo.usedIssued)} ₽</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Подписано, но не выдано</div>
                          <div style={{ fontWeight: 600 }}>{currency(limitInfo.usedLocked)} ₽</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Доход по ведомости</div>
                          <div style={{ fontWeight: 700 }}>{currency(limitInfo.registerIncome)} ₽</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Доступно до подписи</div>
                          <div style={{ fontWeight: 600 }}>{currency(limitInfo.available)} ₽</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>После подписи останется</div>
                          <div style={{ fontWeight: 700, color: limitInfo.forecastAfterSign < 0 ? '#b91c1c' : '#0f172a' }}>
                            {currency(limitInfo.forecastAfterSign)} ₽
                          </div>
                        </div>
                      </div>
                      <div style={{ marginTop: 10, fontSize: 12, color: limitBlockedByFot ? '#b91c1c' : limitWarnByLocked ? '#b45309' : '#475569' }}>
                        {limitBlockedByFot
                          ? `После подписи превышение составит ${currency(Math.abs(limitInfo.forecastAfterSign))} ₽. Уменьшите суммы или дождитесь свободного лимита.`
                          : limitWarnByLocked
                            ? 'С учётом подписанных, но не выданных ведомостей лимит будет исчерпан. Контролируйте выдачу, чтобы освободить лимит.'
                            : 'Данные по лимиту учитывают все выданные и подписанные ведомости месяца.'}
                      </div>
                    </>
                  )}
                  <div style={{ marginTop: 10, fontSize: 12, color: '#475569' }}>ФОТ менеджера: <strong>{currency(managerFot)} ₽</strong></div>
                </div>
              </>
            )}
          </div>

          <div style={{ padding: 16 }}>
            <div style={tableWrapper}>
              {detailLoading ? <div style={{ padding: 20 }}>Загрузка…</div> : (
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, width: 40 }}>#</th>
                      <th style={{ ...thStyle, width: 340 }}>ФИО</th>
                      <th style={{ ...thStyle, width: 220 }}>Должность</th>
                      <th style={{ ...thStyle, width: 100 }}>Дата приёма</th>
                      <th style={{ ...thStyle, width: 100 }}>Дата увольнения</th>
                      <th style={{ ...thStyle, width: 180 }}>Объект</th>
                      <th style={{ ...thStyle, width: 120 }}>Выплачено на карту</th>
                      <th style={{ ...thStyle, width: 120 }}>Аванс</th>
                      <th style={{ ...thStyle, width: 120 }}>Итого доход за мес.</th>
                      <th style={{ ...thStyle, width: 130 }}>К выплате</th>
                      <th style={{ ...thStyle, width: 60 }}>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.rows.map((row, idx) => {
                      const selectedEmployee = row.employeeId
                        ? employees.find((emp) => String(emp.id) === String(row.employeeId))
                        : null;
                      return (
                        <tr
                          key={row.localId}
                          onFocusCapture={() => handleRowFocusChange(row.localId)}
                          onBlurCapture={(event) => handleRowBlurChange(row.localId, event)}
                        >
                          <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 600 }}>{idx + 1}</td>
                        <td style={{ ...tdStyle, minWidth: 300 }}>
                          {isManagerMode ? (
                            <>
                              <input
                                value={row.fullName || ''}
                                onChange={(e) => updateRow(row.localId, { fullName: e.target.value })}
                                placeholder="Название объекта"
                                style={inputStyle}
                                disabled={isLocked}
                              />
                              {!isLocked && (
                                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                                  После выбора менеджера строки подставятся автоматически
                                </div>
                              )}
                            </>
                          ) : (
                            <>
                              {(() => {
                                const employeeSelect = getEmployeeOptions(row);
                                const dropdownVisible = !isLocked && activeEmployeeDropdown === row.localId;
                                const inputValue = employeeSearchMap[row.localId] ?? row.fullName ?? '';
                                return (
                                  <>
                                    <div style={{ position: 'relative', marginBottom: 6 }}>
                                      <input
                                        type="search"
                                        value={inputValue}
                                        onChange={(e) => {
                                          handleEmployeeInputChange(row, e.target.value);
                                          handleEmployeeSearchFocus(row.localId);
                                        }}
                                        onFocus={() => handleEmployeeSearchFocus(row.localId)}
                                        onBlur={handleEmployeeSearchBlur}
                                        placeholder="Начните вводить ФИО, должность, ID или объект"
                                        style={{
                                          ...inputStyle,
                                          display: 'flex',
                                          alignItems: 'center',
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          paddingRight: 24,
                                        }}
                                        className={selectedEmployee?.isDismissed ? 'fired-input' : undefined}
                                        disabled={isLocked}
                                        title={inputValue || ''}
                                      />
                                      {dropdownVisible && (
                                        <div
                                          style={employeeSuggestionListStyle}
                                          onMouseDown={(e) => e.preventDefault()}
                                          onMouseEnter={() => handleEmployeeSearchFocus(row.localId)}
                                        >
                                          {employeeSelect.options.length ? (
                                            employeeSelect.options.map((emp, optionIdx) => {
                                              const dismissed = !!emp.isDismissed;
                                              const baseColor = dismissed ? firedAccentColor : '#0f172a';
                                              return (
                                                <button
                                                  key={`${emp.id}-${optionIdx}`}
                                                  type="button"
                                                  onMouseDown={(e) => e.preventDefault()}
                                                  onClick={() => handleEmployeeOptionPick(row, emp)}
                                                  style={{
                                                    ...employeeSuggestionItemStyle,
                                                    borderBottom: '1px solid #e2e8f0',
                                                    background: dismissed ? '#fff1f2' : '#fff',
                                                    color: baseColor,
                                                  }}
                                                >
                                                  <span style={{ fontWeight: 600 }}>{emp.fullName || 'Без имени'}</span>
                                                  <span style={{ ...employeeSuggestionMetaStyle, color: dismissed ? firedAccentColor : '#475569' }}>
                                                    {(emp.position || '—')}{emp.objectLabel ? ` • ${emp.objectLabel}` : ''}
                                                  </span>
                                                  <span style={{ ...employeeSuggestionMetaStyle, color: dismissed ? firedAccentColor : '#475569' }}>
                                                    Принят: {emp.hireDateLabel || formatEmployeeDate(emp.contractFrom)} · Уволен: {emp.fireDateLabel || formatEmployeeDate(emp.contractTo)}
                                                  </span>
                                                  {dismissed && (
                                                    <span className="no-print" style={{ fontSize: 11, fontWeight: 600, color: firedAccentColor }}>
                                                      Сотрудник уволен
                                                    </span>
                                                  )}
                                                </button>
                                              );
                                            })
                                          ) : (
                                            <div style={{ padding: '12px 14px', fontSize: 12, color: '#64748b' }}>
                                              {employeeSearchMap[row.localId]
                                                ? 'Нет совпадений. Измените запрос или очистите поиск.'
                                                : 'Сотрудники не найдены.'}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                    {inputValue && !employeeSelect.hasMatches && !dropdownVisible && (
                                      <div style={{ fontSize: 12, color: '#b45309', marginBottom: 6 }}>Нет совпадений. Измените запрос или очистите поиск.</div>
                                    )}
                                      {row.employeeId && (
                                        <button
                                          type="button"
                                          onClick={() => handleEmployeeSelect(row, null)}
                                          style={{ fontSize: 12, color: '#2563eb', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
                                        >
                                          Отвязать сотрудника
                                        </button>
                                      )}
                                  </>
                                );
                              })()}
                            </>
                          )}
                        </td>
                        <td style={tdStyle}>
                          {isManagerMode ? (
                            <input
                              value={row.position || ''}
                              onChange={(e) => updateRow(row.localId, { position: e.target.value })}
                              placeholder="Код объекта или примечание"
                              style={inputStyle}
                              disabled={isLocked}
                            />
                          ) : row.employeeId ? (
                            <div style={{ padding: '6px 8px', borderRadius: 8, background: '#f1f5f9', border: '1px solid #e2e8f0' }}>{row.position || '—'}</div>
                          ) : (
                            <>
                              <select
                                value={positions.find((p) => p.name === row.position)?.id || ''}
                                onChange={(e) => handleManualPosition(row, e.target.value)}
                                style={selectStyle}
                                disabled={isLocked}
                              >
                                <option value="">Выберите должность…</option>
                                {positions.map((pos) => (
                                  <option key={pos.id} value={pos.id}>{pos.name}</option>
                                ))}
                              </select>
                              <input
                                value={row.position || ''}
                                onChange={(e) => updateRow(row.localId, { position: e.target.value })}
                                placeholder="Если нет в списке — впишите"
                                style={{ ...inputStyle, marginTop: 6 }}
                                disabled={isLocked}
                              />
                            </>
                          )}
                        </td>
                        <td style={{ ...tdStyle, minWidth: 110 }}>
                          {selectedEmployee ? (
                            <span
                              style={employeeMetaValueStyle}
                              className={selectedEmployee.isDismissed ? 'fired-text' : undefined}
                            >
                              {selectedEmployee.hireDateLabel || '—'}
                            </span>
                          ) : (
                            <span style={employeeMetaValueStyle}>—</span>
                          )}
                        </td>
                        <td style={{ ...tdStyle, minWidth: 110 }}>
                          {selectedEmployee ? (
                            <span
                              style={employeeMetaValueStyle}
                              className={selectedEmployee.isDismissed ? 'fired-text' : undefined}
                            >
                              {selectedEmployee.fireDateLabel || '—'}
                            </span>
                          ) : (
                            <span style={employeeMetaValueStyle}>—</span>
                          )}
                        </td>
                        <td style={{ ...tdStyle, minWidth: 160 }}>
                          {selectedEmployee ? (
                            <span
                              style={employeeMetaValueStyle}
                              className={selectedEmployee.isDismissed ? 'fired-text' : undefined}
                            >
                              {selectedEmployee.objectDisplay || selectedEmployee.workObject || selectedEmployee.objectLabel || '—'}
                            </span>
                          ) : (
                            <span style={employeeMetaValueStyle}>—</span>
                          )}
                        </td>
                        {['paidToCard','advanceAmount','incomeAmount'].map((key) => (
                          <td key={key} style={{ ...tdStyle, minWidth: 110 }}>
                            <input
                              type="text"
                              value={row[key]}
                              onChange={(e) => updateRow(row.localId, { [key]: e.target.value })}
                              style={inputStyle}
                              disabled={isLocked}
                              placeholder="0,00"
                            />
                          </td>
                        ))}
                        <td style={{ ...tdStyle, minWidth: 130 }}>
                          <input
                            type="text"
                            value={currency(calcToPayAmount(row))}
                            readOnly
                            style={{ ...inputStyle, background: '#f8fafc', fontWeight: 600, textAlign: 'right' }}
                          />
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          <button onClick={() => removeRow(row.localId)} disabled={isLocked} style={{ border: 'none', background: 'transparent', color: isLocked ? '#cbd5f5' : '#ef4444', cursor: isLocked ? 'not-allowed' : 'pointer' }}>✕</button>
                        </td>
                      </tr>
                    );
                    })}
                    <tr>
                      <td style={{ ...tdStyle, fontWeight: 700 }} colSpan={6}>Итого</td>
                      <td style={{ ...tdStyle, fontWeight: 700, textAlign: 'right' }}>{currency(totals.paidToCard)}</td>
                      <td style={{ ...tdStyle, fontWeight: 700, textAlign: 'right' }}>{currency(totals.advanceAmount)}</td>
                      <td style={{ ...tdStyle, fontWeight: 700, textAlign: 'right' }}>{currency(totals.incomeAmount)}</td>
                      <td style={{ ...tdStyle, fontWeight: 700, textAlign: 'right' }}>{currency(totals.toPayAmount)}</td>
                      <td style={tdStyle}></td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div style={{ padding: '0 16px 16px', display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            <button onClick={() => saveRegister()} disabled={saving || isLocked} style={{ padding: '10px 16px', borderRadius: 10, border: '1px solid #16a34a', background: isLocked ? '#bbf7d0' : '#22c55e', color: '#fff', fontWeight: 600 }}>{saving ? 'Сохранение…' : 'Сохранить ведомость'}</button>
            {form.id && !isLocked && (
              <button onClick={handleDelete} style={{ padding: '10px 16px', borderRadius: 10, border: '1px solid #ef4444', background: '#fff1f2', color: '#b91c1c' }}>Удалить</button>
            )}
            {canSign && (
              <button
                onClick={handleSign}
                disabled={signing || limitBlockedByFot}
                title={limitBlockedByFot ? 'Превышен лимит ФОТ — подпись недоступна' : ''}
                style={{
                  padding: '10px 16px',
                  borderRadius: 10,
                  border: '1px solid #0ea5e9',
                  background: limitBlockedByFot ? '#f1f5f9' : '#e0f2fe',
                  color: limitBlockedByFot ? '#94a3b8' : '#0369a1',
                  cursor: limitBlockedByFot ? 'not-allowed' : 'pointer',
                }}
              >
                {signing ? 'Подписание…' : 'Подписать ГД'}
              </button>
            )}
            {canIssue && (
              <button onClick={handleIssue} disabled={issuing} style={{ padding: '10px 16px', borderRadius: 10, border: '1px solid #2563eb', background: '#dbeafe', color: '#1d4ed8' }}>{issuing ? 'Выдача…' : 'Выдать'}</button>
            )}
            {canUnlock && (
              <button onClick={handleUnlock} disabled={unlocking} style={{ padding: '10px 16px', borderRadius: 10, border: '1px solid #f97316', background: '#fff7ed', color: '#c2410c' }}>{unlocking ? 'Возврат…' : 'Снять подпись'}</button>
            )}
            <div style={{ flex: 1 }} />
            <button
              onClick={() => handlePrint(isManagerMode ? 'manager-with-objects' : 'full')}
              style={{ padding: '10px 16px', borderRadius: 10, border: '1px solid #0ea5e9', background: '#e0f2fe', color: '#0369a1' }}
            >
              {isManagerMode ? 'Печать менеджера с объектами' : 'Распечатать ведомость полностью'}
            </button>
            <button
              onClick={() => handlePrint(isManagerMode ? 'manager-only' : 'per-employee')}
              style={{ padding: '10px 16px', borderRadius: 10, border: '1px solid #7c3aed', background: '#ede9fe', color: '#5b21b6' }}
            >
              {isManagerMode ? 'Печать только менеджера' : 'Распечатать по отдельности'}
            </button>
            {statusMessage && (
              <span style={{ alignSelf: 'center', color: '#059669', fontWeight: 600 }}>{statusMessage}</span>
            )}
            {limitBlockedByFot && !statusMessage && (
              <span style={{ alignSelf: 'center', color: '#b91c1c', fontWeight: 600 }}>
                Превышен лимит на {currency(Math.abs(limitInfo.forecastAfterSign))} ₽ — подпись заблокирована.
              </span>
            )}
          </div>
        </div>
      </div>
  );
}
